import { create } from "zustand";

// sessionStorage-backed call counter — a RELOAD-safe fallback for priorCallCount.
// The API count can be stale (a just-finished call may not be indexed yet) and
// the in-memory offer store resets on reload; this survives both. Bumped on every
// call end and read as a fallback so the pay button shows from the 2nd call on.
const SESSION_CALL_COUNT_PREFIX = "callCount_";
export const bumpSessionCallCount = (agentId) => {
  if (!agentId) return;
  try {
    const k = `${SESSION_CALL_COUNT_PREFIX}${agentId}`;
    const n = (parseInt(sessionStorage.getItem(k), 10) || 0) + 1;
    sessionStorage.setItem(k, String(n));
  } catch (_e) {
    /* ignore */
  }
};
export const getSessionCallCount = (agentId) => {
  if (!agentId) return 0;
  try {
    return parseInt(sessionStorage.getItem(`${SESSION_CALL_COUNT_PREFIX}${agentId}`), 10) || 0;
  } catch (_e) {
    return 0;
  }
};

// Drives the sales agent's live "select_offer" tool. The socket listener
// (initNotificationSocket) writes here via getState().setOffer(tier); the
// go-live screens read it reactively:
//   tier "plans" → GoLivePayg / TrialOffer open the ChoosePlanModal ($119–$599)
//   tier "trial" → GoLiveVariant shows the 21-day trial
//   tier "payg"  → GoLiveVariant shows the $5/month PAYG
//
// `nonce` bumps on every set so the same tier fired twice still re-triggers the
// effect (e.g. agent says "plans" again after the user closed the modal).
export const useOfferStore = create((set) => ({
  forcedOffer: null, // "plans" | "trial" | "payg" | null
  // Plan the agent recommended (e.g. "Scaler") → pre-selected in the plan modal.
  recommendedPlan: null,
  nonce: 0,
  // Set by the sales agent's go_live_intent tool the moment the owner is
  // satisfied/interested. It collapses the /fast-step "Meet Sophia" popup into
  // the persistent banner ON THE SAME SCREEN — no page change, no plan modal.
  // The actual offers come afterwards via setOffer (plans/trial/payg).
  promoted: false,
  promote: () => set({ promoted: true }),
  // Set by the sales agent's silent_mode tool when the user asks it to cut/stop
  // the call. It closes the "Meet" call popup WITHOUT ending the call (the call
  // keeps running in the banner). `silentNonce` bumps each time so repeated
  // requests still re-fire the effect. The agent then stays quiet on its own
  // (the tool has speak_after_execution:false) until the user speaks again.
  silenced: false,
  silentNonce: 0,
  goSilent: () =>
    set((s) => ({ silenced: true, silentNonce: s.silentNonce + 1 })),
  // Set by the sales agent's confirm_go_live tool the moment the owner CLEARLY
  // agrees to proceed with the shown offer ("yes, I want this plan / let's go
  // live"). The $5 (GoLivePayg) and $20 (TrialOffer) pay buttons stay HIDDEN
  // until this flips true. Reset to false whenever a new offer screen is shown
  // (setOffer) so each fresh offer starts hidden again.
  // Which offer TIERS the owner has agreed to (confirm_go_live), e.g.
  // { payg: true, trial: true }. Per-tier — NOT a single global flag — so:
  //   • switching to a plan they haven't agreed to yet hides its button again,
  //   • returning to an already-agreed tier keeps the button visible.
  // setAgreed marks the tier currently on screen (forcedOffer). This map PERSISTS
  // across offer switches (setOffer does NOT reset it).
  agreedTiers: {},
  setAgreed: () =>
    set((s) =>
      s.forcedOffer
        ? { agreedTiers: { ...s.agreedTiers, [s.forcedOffer]: true } }
        : {}
    ),
  // Set true once the live sales call has actually ENDED (call_ended). Distinct
  // from "no live call right now" — a fresh visit with no call yet keeps this
  // false so the pay button stays hidden until a call happens and ends. Reveals
  // the pay buttons after the call. Stays true for the rest of the session (so a
  // 2nd call also shows the button).
  callEnded: false,
  markCallEnded: () => set({ callEnded: true }),
  // Number of calls this agent has ALREADY had (from agent_calls_index). Fetched
  // on /fast-step load so the "first-ever call" button gating survives a page
  // reload: count > 0 means the owner has tested before → keep the Go Live button
  // visible even during a live call. null = not fetched yet.
  priorCallCount: null,
  setPriorCallCount: (n) => set({ priorCallCount: n }),
  // Cleared when the call ends so a fresh call isn't stuck silent. While
  // `silenced` is true, CallRouteGuide does NOT inject route guidance — a
  // navigation must not force the (silent) agent to speak.
  clearSilent: () => set({ silenced: false }),
  setOffer: (tier, recommendedPlan = null) =>
    set((s) => ({
      forcedOffer: tier,
      recommendedPlan: recommendedPlan || s.recommendedPlan,
      nonce: s.nonce + 1,
      promoted: true,
      // NOTE: do NOT reset agreedTiers here — agreement is per-tier and must
      // survive switching between offers (e.g. $5 → $21 → back to $5).
    })),
  clearOffer: () => set({ forcedOffer: null }),
}));
